// Preset math for the publisher Overview metrics section (CTV-530).
// Presets mirror the internal dashboard's DateRangeSelector so the two
// surfaces read as siblings. Baseline is "yesterday" (today - 1 day) —
// the current day is partial, so it would understate CPM/net.
// Returned start/end are local-time YYYY-MM-DD strings.

export type DateRangePreset =
  | 'yesterday'
  | '7days'
  | '30days'
  | 'mtd'
  | 'lm'
  | 'ytd'
  | 'custom';

const pad = (n: number) => n.toString().padStart(2, '0');

export const toYmdLocal = (d: Date): string =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

const addDays = (d: Date, days: number): Date => {
  const out = new Date(d);
  out.setDate(out.getDate() + days);
  return out;
};

export function computePresetRange(
  preset: Exclude<DateRangePreset, 'custom'>,
  today: Date,
): { start: string; end: string } {
  const yesterday = addDays(today, -1);
  const end = toYmdLocal(yesterday);

  switch (preset) {
    case 'yesterday':
      return { start: end, end };
    case '7days':
      return { start: toYmdLocal(addDays(yesterday, -6)), end };
    case '30days':
      return { start: toYmdLocal(addDays(yesterday, -29)), end };
    case 'mtd': {
      const start = new Date(yesterday.getFullYear(), yesterday.getMonth(), 1);
      return { start: toYmdLocal(start), end };
    }
    case 'lm': {
      // Last full calendar month: 1st through last-day of the previous month.
      const first = new Date(yesterday.getFullYear(), yesterday.getMonth() - 1, 1);
      const last = new Date(yesterday.getFullYear(), yesterday.getMonth(), 0);
      return { start: toYmdLocal(first), end: toYmdLocal(last) };
    }
    case 'ytd': {
      const start = new Date(yesterday.getFullYear(), 0, 1);
      return { start: toYmdLocal(start), end };
    }
  }
}

// Short labels matching the internal dashboard's DateRangeSelector so a
// publisher who's used both surfaces recognizes them.
export const PRESET_LABELS: Record<DateRangePreset, string> = {
  yesterday: 'Yesterday',
  '7days': 'L7D',
  '30days': 'L30D',
  mtd: 'MTD',
  lm: 'LM',
  ytd: 'YTD',
  custom: 'Custom',
};

export const VISIBLE_PRESETS: DateRangePreset[] = [
  'yesterday',
  '7days',
  '30days',
  'mtd',
  'lm',
  'ytd',
];

// MM/DD/YYYY for the pill-button display, matching the internal selector.
export function formatMmDdYyyy(iso: string): string {
  const [y, m, d] = iso.split('-');
  return `${m}/${d}/${y}`;
}

// ISO parse that treats the string as local (avoids UTC shifting).
export function parseIsoLocal(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
}
